import DOMPurify from 'dompurify';
import type { Ticket, Message } from './types';

// Finner den opprinnelige kundemeldingen (første KUNDE_MELDING kronologisk).
// Returneres kun når den faktisk er sakens "opprinnelige henvendelse" –
// dvs. e-post-saker, eller når beskrivelsen er en kopi av meldingsteksten.
export function getOriginalMessage(ticket: Ticket): Message | undefined {
  const first = [...(ticket.messages ?? [])]
    .filter((m) => m.type === 'KUNDE_MELDING')
    .sort(
      (a, b) =>
        Date.parse(a.opprettet) - Date.parse(b.opprettet) || a.id.localeCompare(b.id),
    )[0];
  if (!first) return undefined;
  const normalize = (value?: string | null) => (value ?? '').replace(/\s+/g, ' ').trim();
  return ticket.kanal === 'EPOST' ||
    !ticket.beskrivelse ||
    normalize(first.innholdTekst) === normalize(ticket.beskrivelse)
    ? first
    : undefined;
}

// Sanitiserer e-post-HTML for trygg visning: DOMPurify fjerner farlig innhold
// først, deretter hvitelistes inline-stiler manuelt slik at ingen url(),
// posisjonering eller host-påvirkende stiler slipper gjennom.
export function sanitizeEmailHtml(html: string): string {
  const fragment = DOMPurify.sanitize(html, {
    RETURN_DOM_FRAGMENT: true,
    ALLOWED_TAGS: [
      'p', 'br', 'div', 'span', 'b', 'strong', 'i', 'em', 'u', 's', 'strike',
      'a', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre',
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'caption', 'colgroup', 'col',
      'img', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'sup', 'sub',
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'style', 'colspan', 'rowspan', 'start', 'value', 'scope'],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'meta', 'link', 'svg', 'math'],
  });

  for (const element of Array.from(fragment.querySelectorAll<HTMLElement>('[style]'))) {
    const style = element.style;
    const allowed: Record<string, RegExp> = {
      'font-weight': /^(normal|bold|[1-9]00)$/,
      'font-style': /^(normal|italic|oblique)$/,
      'text-decoration': /^(none|underline|line-through|underline line-through)$/,
      'text-align': /^(left|right|center|justify)$/,
      'white-space': /^(normal|pre-wrap|pre-line)$/,
      'vertical-align': /^(baseline|top|middle|bottom|sub|super)$/,
    };
    const keep = Object.entries(allowed).flatMap(([name, pattern]) => {
      const value = style.getPropertyValue(name).trim();
      return pattern.test(value) ? [[name, value]] : [];
    });
    element.removeAttribute('style');
    for (const [name, value] of keep) element.style.setProperty(name, value);
  }

  for (const link of Array.from(fragment.querySelectorAll('a[href]'))) {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  }

  for (const image of Array.from(fragment.querySelectorAll('img'))) {
    const src = image.getAttribute('src') ?? '';
    if (!/^(https?:\/\/|\/api\/|data:image\/(?:png|jpeg|gif|webp);base64,)/i.test(src)) {
      image.removeAttribute('src');
    }
    image.setAttribute('loading', 'lazy');
    image.setAttribute('referrerpolicy', 'no-referrer');
  }

  const container = document.createElement('div');
  container.append(fragment);
  return container.innerHTML;
}
